import { useGet } from "@albertine/api-client";
import type { ListAllMembersItem } from "@albertine/types/openapi/lmt";
import { type ThemePreference, ThemePreferenceTextMap } from "@albertine/types/theme";
import { useState } from "react";
import { useMembersMap } from "../../common/hooks/useMembers";
import { mergeThemePreferences } from "../../common/utils/preferences.util";
import { useThemePreviewUrlQueryState } from "../../utils/customHook.util";
import { performOrOperatorSearch } from "../../utils/filter.util";
import { nameWithoutTitle } from "../../utils/members.util";
import Autocomplete from "../Common/Autocomplete";
import { MemberAvatar } from "../Common/Avatar";
import { Flex, FlexInline } from "../Common/Flex";
import Icon from "../Common/Icon";
import Label from "../Common/Label";
import Tag from "../Common/Tag";
import ThemeList, { ThemeListLoading } from "./ThemeList";
import "./PreviewThemes.css";

function MemberThemes(props: { memberId: string | undefined | null }) {
    const { memberId } = props;

    const { data: memberDiscoverThemes } = useGet(
        "previewMemberDiscoverThemes",
        {
            param: { memberId: memberId ?? "" },
        },
        {
            enabled: !!memberId,
        },
    );

    if (!memberId || !memberDiscoverThemes) {
        return <ThemeListLoading ungroupedThemesHeading="Personalised themes" />;
    }

    return (
        <ThemeList
            spotlightThemes={
                memberDiscoverThemes.spotlightTheme
                    ? [memberDiscoverThemes.spotlightTheme]
                    : []
            }
            cityGuides={memberDiscoverThemes.cityGuideThemes}
            articlesSource={{ type: "memberPreview", memberId: memberId }}
            themeGroups={{}}
            ungrouped={{
                group: null,
                themes: memberDiscoverThemes.personalisedThemes,
            }}
            ungroupedThemesHeading="Personalised themes"
            onArticleClick={undefined}
            onThemeEditClick={undefined}
        />
    );
}

function MemberPreferenceTags(props: { memberId: string }) {
    const { memberId } = props;
    const { data: memberPreferences } = useGet(
        "memberPreferences",
        { param: { memberId } },
        { enabled: !!memberId },
    );

    const topCities = (memberPreferences?.topCities || []).map((city) => (
        <Tag.Small.P90 key={city.city}>
            {" "}
            <FlexInline gap="small">
                <Icon.LocationOn.XSmall /> {city.city}
            </FlexInline>
        </Tag.Small.P90>
    ));
    const scoreMap = mergeThemePreferences(memberPreferences);
    const preferences: ThemePreference[] = Object.keys(scoreMap) as ThemePreference[];
    const lovedPreferences = preferences.filter((p) => scoreMap[p] > 1);
    const lovedTags = lovedPreferences.map((preference) => (
        <Tag.Small.P90 key={preference}>
            <FlexInline gap="small">
                <Icon.Favorite.XSmall />
                {ThemePreferenceTextMap[preference] || preference}
            </FlexInline>
        </Tag.Small.P90>
    ));

    const likedPreferences = preferences.filter((p) => scoreMap[p] === 1);
    const likedTags = likedPreferences.map((preference) => (
        <Tag.Small.P90 key={preference}>
            <FlexInline gap="small">
                <Icon.ThumbUp.XSmall />
                {ThemePreferenceTextMap[preference] || preference}
            </FlexInline>
        </Tag.Small.P90>
    ));

    const positiveTags = [...topCities, ...lovedTags, ...likedTags];

    const negativePreferences = preferences.filter((p) => scoreMap[p] < 0);
    const negativeTags = negativePreferences.map((preference) => (
        <Tag.Small.R80 key={preference}>
            <FlexInline gap="small">
                <Icon.ThumbDown.XSmall />
                {ThemePreferenceTextMap[preference] || preference}
            </FlexInline>
        </Tag.Small.R80>
    ));

    return (
        <Flex.Row gap="medium" wrap="wrap">
            <Label.Small.N50>Positive:</Label.Small.N50>
            <div>
                <Flex.Row gap="small">
                    {positiveTags.length > 0 ? (
                        positiveTags
                    ) : (
                        <Label.Small.Light.N60>-</Label.Small.Light.N60>
                    )}
                </Flex.Row>
            </div>
            <Label.Small.N50>Negative:</Label.Small.N50>
            <div>
                <Flex.Row gap="small">
                    {negativeTags.length > 0 ? (
                        negativeTags
                    ) : (
                        <Label.Small.Light.N60>-</Label.Small.Light.N60>
                    )}
                </Flex.Row>
            </div>
        </Flex.Row>
    );
}

function PreviewThemes() {
    const membersMap = useMembersMap();
    const { urlState, setUrlState } = useThemePreviewUrlQueryState();

    const member = membersMap[urlState.memberId ?? ""];

    const [selectedMember, setSelectedMember] = useState<ListAllMembersItem>();

    return (
        <Flex.Column gap="large" className="preview-themes">
            <Flex.Row gap="medium">
                <Flex.Column gap="medium">
                    <Autocomplete<ListAllMembersItem>
                        placeholder="Select member..."
                        className="preview-themes__member-autocomplete"
                        label="Member name"
                        initialSelection={member || selectedMember}
                        results={(searchTerm: string) =>
                            Promise.resolve(
                                performOrOperatorSearch<ListAllMembersItem>(
                                    searchTerm,
                                    2,
                                    [nameWithoutTitle],
                                    Object.values(membersMap),
                                ),
                            )
                        }
                        renderSingleResult={(result) => (
                            <>
                                <MemberAvatar size="small" memberId={result.id} />
                                <span>{`${result.firstName} ${result.lastName}`}</span>
                            </>
                        )}
                        onSelect={(m) => {
                            setSelectedMember(m);
                            setUrlState({ memberId: m?.id });
                        }}
                    />
                </Flex.Column>
            </Flex.Row>
            {urlState.memberId && <MemberPreferenceTags memberId={urlState.memberId} />}
            <MemberThemes memberId={urlState.memberId} />
        </Flex.Column>
    );
}

export default PreviewThemes;
